import type { ErrorComponentProps } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
  ScriptOnce,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";
import { ThemeProvider, createThemeInitScript } from "@/components/site/theme";

const THEME_STORAGE_KEY = "goproprojects.theme";
const THEME_DEFAULT = "light";

import headingFont from "@fontsource/poppins/files/poppins-latin-700-normal.woff2?url";
import bodyFont from "@fontsource/manrope/files/manrope-latin-400-normal.woff2?url";
import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { LazyToaster } from "@/components/site/lazy-toaster";
import { SmoothScroll } from "@/components/site/SmoothScroll";
import { localBusinessJsonLd } from "@/content/site";
import { WebVitalsReporter } from "@/components/site/web-vitals-reporter";
import { AnalyticsTracker } from "@/components/site/analytics-tracker";
import { CookieConsent } from "@/components/site/cookie-consent";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Pagina niet gevonden</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          De pagina die u zoekt bestaat niet of is verplaatst.
        </p>
        <div className="mt-6">
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Terug naar home
          </a>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          Deze pagina kon niet geladen worden
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Er ging iets mis aan onze kant. Probeer opnieuw of ga terug naar home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Opnieuw proberen
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  staticData: { sitemap: false },
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Gopro Projects — Bouwkundige Zwembaden & Renovatie" },
      { name: "description", content: "Specialist in bouwkundige zwembaden, polyester monoblock renovatie, zwembadtechniek en lekdetectie in Limburg en Zuid-Nederland." },
      { name: "author", content: "Gopro Projects" },
      { property: "og:title", content: "Gopro Projects — Bouwkundige Zwembaden & Renovatie" },
      { property: "og:description", content: "Specialist in bouwkundige zwembaden, polyester renovatie en zwembadtechniek." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "preload", as: "font", type: "font/woff2", href: headingFont, crossOrigin: "anonymous" },
      { rel: "preload", as: "font", type: "font/woff2", href: bodyFont, crossOrigin: "anonymous" },
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/png", href: "/favicon.png" },
    ],
    scripts: [localBusinessJsonLd()],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const match = /^\/(nl|en|fr)(?=\/|$)/.exec(pathname);
  const lang = match ? match[1] : "nl";

  return (
    <html lang={lang} suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        <ScriptOnce>
          {createThemeInitScript({ storageKey: THEME_STORAGE_KEY, defaultTheme: THEME_DEFAULT })}
        </ScriptOnce>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider storageKey={THEME_STORAGE_KEY} defaultTheme={THEME_DEFAULT}>
        <div className="site-shell flex min-h-screen flex-col">
          <Header />
          <main className="flex-1">
            <Outlet />
          </main>
          <Footer />
        </div>
        <LazyToaster />
        <SmoothScroll />
        <WebVitalsReporter />
        <AnalyticsTracker />
        <CookieConsent />
      </ThemeProvider>
    </QueryClientProvider>
  );
}
